/**
 * Base API Class
 * Provides standard CRUD operations for all entities.
 */
import { ApiError } from '../errors/api-error.js';

export class BaseApi {
    /**
     * @param {Object} client - HTTP Client instance
     * @param {string} basePath - Base path for the entity (e.g., '/weaknesses')
     */
    constructor(client, basePath) {
        this.client = client;
        this.basePath = basePath;
    }

    /**
     * Get all items with optional pagination/filtering
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await this.client.get(this.basePath, { params });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id - Item ID
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await this.client.get(`${this.basePath}/${id}`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Create new item
     * @param {Object} data - Item data
     * @returns {Promise<Object>}
     */
    async create(data) {
        try {
            const response = await this.client.post(this.basePath, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Update item
     * @param {number|string} id - Item ID
     * @param {Object} data - Updated data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        try {
            const response = await this.client.put(`${this.basePath}/${id}`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Delete item
     * @param {number|string} id - Item ID
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.client.delete(`${this.basePath}/${id}`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Delete multiple items
     * @param {Array<number|string>} ids - Array of item IDs
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.client.post(`${this.basePath}/batch-delete`, { ids });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * Batch partial update (resource exposes POST /batch-update).
     * @param {Array<number|string>} ids
     * @param {Object} patch
     */
    async batchUpdate(ids, patch) {
        try {
            const response = await this.client.post(`${this.basePath}/batch-update`, { ids, patch });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    /**
     * GET under base path (e.g. /entity/summary). Path must start with /.
     * @param {string} relativePath
     */
    async getSubresource(relativePath) {
        try {
            const suffix = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
            const response = await this.client.get(`${this.basePath}${suffix}`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }
}
